import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Tag, Store, MapPin, Layers, ArrowRight, Loader2 } from "lucide-react";

const ICONS = {
  category: Layers,
  city: MapPin,
  vendor: Store,
  product: Tag,
};

/**
 * Google-style autocomplete on top of /api/search/suggest.
 *
 * Props:
 *   value, onChange      — controlled string (the visible query).
 *   onSubmit(q)          — called when user hits Enter (or clicks search).
 *   placeholder, autoFocus, testId, size (h-9 | h-12).
 *
 * Behaviour:
 *   - 180ms debounce while typing.
 *   - Up/Down navigates suggestions, Enter accepts.
 *   - Clicking a suggestion either navigates to its href (categories / cities /
 *     vendors) OR fills the input and submits (products → search).
 *   - Closes on outside click + Esc.
 */
export default function SearchAutocomplete({
  value,
  onChange,
  onSubmit,
  placeholder = "Search business, service, product, vendor, city…",
  autoFocus = false,
  testId = "search-autocomplete",
  size = "h-12",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!value || value.trim().length < 2) { setItems([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search/suggest`, { params: { q: value } });
        setItems(data || []);
        setActive(-1);
        setOpen(true);
      } catch (_) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (it) => {
    setOpen(false);
    if (it.href) {
      navigate(it.href);
    } else {
      onChange?.(it.label);
      onSubmit?.(it.label);
    }
  };

  const onKey = (e) => {
    if (!open || items.length === 0) {
      if (e.key === "Enter") { onSubmit?.(value); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0) choose(items[active]);
      else onSubmit?.(value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative flex-1" data-testid={testId}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKey}
        onFocus={() => items.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={`pl-9 pr-9 ${size} rounded-full`}
        data-testid={`${testId}-input`}
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
      )}

      {open && items.length > 0 && (
        <div
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 rounded-2xl border border-border/60 bg-popover shadow-xl overflow-hidden"
          data-testid={`${testId}-list`}
        >
          {items.map((it, i) => {
            const Icon = ICONS[it.type] || Tag;
            const isActive = i === active;
            return (
              <button
                type="button"
                key={`${it.type}-${it.label}-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(it)}
                className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm ${
                  isActive ? "bg-primary/10" : "hover:bg-muted"
                }`}
                data-testid={`${testId}-item-${i}`}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.label}</div>
                  {it.sub && <div className="text-xs text-muted-foreground truncate">{it.sub}</div>}
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
